import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import myAxios from "../AxiosUrl";
import "./Cache.css";
import Loading from "../Loading/Loading";
import { getAdminPermissions } from "../../utils/adminPermissions";

const Cache = () => {
  const [cacheData, setCacheData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Statistics

  const [stats, setStats] = useState({
    totalCacheSize: 0,
    totalItems: 0,
    averageItemSize: 0,
    cacheTypes: {},
  });

  // Modal state
  const [selectedCacheItem, setSelectedCacheItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // 'single' or 'all'
  const [deleting, setDeleting] = useState(false);

  // Refresh controls
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { isSuperAdmin } = getAdminPermissions();

  const itemsPerPage = 20;

  useEffect(() => {
    fetchCacheData();
  }, [currentPage, searchTerm]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchCacheData();
      }, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, currentPage, searchTerm]);

  const fetchCacheData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await myAxios.get(`/cache?${params}`);

      if (response.data.success) {
        setCacheData(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalCount);
        setStats({
          totalCacheSize: response.data.statistics.totalSize,
          totalItems: response.data.statistics.totalKeys,
          averageItemSize: response.data.statistics.averageSize,
          cacheTypes: response.data.statistics.typeBreakdown,
        });
        setError(null);

        // If current page is empty and we have previous pages, go to the last page
        if (
          response.data.data.length === 0 &&
          currentPage > 1 &&
          response.data.totalPages > 0
        ) {
          setCurrentPage(response.data.totalPages);
        }
      } else {
        setError("Failed to fetch cache data");
      }
    } catch (err) {
      setError("Error fetching cache data");
      console.error("Error fetching cache data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCacheData();
    setRefreshing(false);
  };

  const handleDeleteSingle = async (key) => {
    setDeleting(true);
    try {
      const response = await myAxios.delete(
        `/cache/${encodeURIComponent(key)}`
      );

      if (response.data.success) {
        // Refresh the data to get updated cache
        await fetchCacheData();

        // Close modal and reset state
        setShowDeleteConfirm(false);
        setDeleteTarget(null);

        // Show success message
        toast.success(`Cache item "${key}" deleted successfully!`);

        // Reset refreshing state to ensure auto-refresh works properly
        setRefreshing(false);
      } else {
        setError("Failed to delete cache item");
        toast.error("Failed to delete cache item");
      }
    } catch (err) {
      setError("Error deleting cache item");
      toast.error("Error deleting cache item");
      console.error("Error deleting cache item:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!isSuperAdmin) {
      toast.error("Only super admins can clear all cache");
      setShowDeleteConfirm(false);
      return;
    }

    setDeleting(true);
    try {
      const response = await myAxios.delete("/cache");

      if (response.data.success) {
        // Refresh the data to get updated cache state
        await fetchCacheData();

        // Close modal and reset state
        setShowDeleteConfirm(false);
        setDeleteTarget(null);

        // Reset pagination to first page
        setCurrentPage(1);

        // Show success message
        toast.success("All cache data cleared successfully!");

        // Reset refreshing state to ensure auto-refresh works properly
        setRefreshing(false);
      } else {
        setError("Failed to clear all cache");
        toast.error("Failed to clear all cache");
      }
    } catch (err) {
      setError("Error clearing all cache");
      toast.error("Error clearing all cache");
      console.error("Error clearing all cache:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCacheItemClick = (item) => {
    setSelectedCacheItem(item);
    setShowModal(true);
  };

  const confirmDelete = (key, type) => {
    if (type === "all" && !isSuperAdmin) {
      toast.error("Only super admins can clear all cache");
      return;
    }

    setDeleteTarget(key);
    setDeleteType(type);
    setShowDeleteConfirm(true);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatValue = (value) => {
    if (typeof value === "string") {
      return value.length > 100 ? value.substring(0, 100) + "..." : value;
    }
    if (typeof value === "object") {
      return JSON.stringify(value).length > 100
        ? JSON.stringify(value).substring(0, 100) + "..."
        : JSON.stringify(value);
    }
    return String(value);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "object":
        return "primary";
      case "array":
        return "success";
      case "string":
        return "info";
      case "number":
        return "warning";
      default:
        return "secondary";
    }
  };

  return (
    <div className="cache-container">
      <div className="cache-header">
        <h1>Cache Management</h1>
        <div className="cache-actions">
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh (30s)
          </label>
          <button
            className="btn btn-danger"
            onClick={() => confirmDelete(null, "all")}
            disabled={!isSuperAdmin}
            title={
              isSuperAdmin
                ? "Clear all cache"
                : "Super admin permission required"
            }
          >
            Clear All Cache
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">storage</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {formatBytes(stats.totalCacheSize)}
            </div>
            <div className="stat-label">Total Cache Size</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">inventory</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.totalItems.toLocaleString()}
            </div>
            <div className="stat-label">Total Items</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">speed</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {formatBytes(stats.averageItemSize)}
            </div>
            <div className="stat-label">Average Item Size</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">category</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {Object.keys(stats.cacheTypes || {}).length}
            </div>
            <div className="stat-label">Cache Types</div>
          </div>
        </div>
      </div>

      {/* Cache Types Breakdown */}
      {stats.cacheTypes && Object.keys(stats.cacheTypes).length > 0 && (
        <div className="cache-types-section">
          <h3>Cache Types Breakdown</h3>
          <div className="cache-types-grid">
            {Object.entries(stats.cacheTypes).map(([type, count]) => (
              <div key={type} className="cache-type-card">
                <span className={`type-badge ${getTypeColor(type)}`}>
                  {type}
                </span>
                <span className="type-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="cache-search">
        <div className="search-group">
          <span className="material-icons">search</span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search cache keys or values..."
          />
        </div>
      </div>

      {/* Cache Table */}
      <div className="cache-table-container">
        {loading ? (
          <Loading />
        ) : error ? (
          <div className="error">{error}</div>
        ) : cacheData.length === 0 ? (
          <div className="empty-state">
            <span className="material-icons">inbox</span>
            <p>No cache data found</p>
          </div>
        ) : (
          <table className="cache-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Type</th>
                <th>Size</th>
                <th>Value Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cacheData.map((item, index) => (
                <tr key={index}>
                  <td className="cache-key">{item.key}</td>
                  <td>
                    <span className={`type-badge ${getTypeColor(item.type)}`}>
                      {item.type}
                    </span>
                  </td>
                  <td className="cache-size">{formatBytes(item.size)}</td>
                  <td className="cache-value">{formatValue(item.value)}</td>
                  <td className="cache-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleCacheItemClick(item)}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => confirmDelete(item.key, "single")}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages} ({totalItems} total items)
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn btn-secondary"
        >
          Next
        </button>
      </div>

      {/* Cache Item Details Modal */}
      {showModal && selectedCacheItem && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cache Item Details</h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="cache-details">
                <div className="detail-row">
                  <strong>Key:</strong> {selectedCacheItem.key}
                </div>
                <div className="detail-row">
                  <strong>Type:</strong>
                  <span
                    className={`type-badge ${getTypeColor(
                      selectedCacheItem.type
                    )}`}
                  >
                    {selectedCacheItem.type}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Size:</strong> {formatBytes(selectedCacheItem.size)}
                </div>
                <div className="detail-row">
                  <strong>Value:</strong>
                  <pre className="cache-value-full">
                    {typeof selectedCacheItem.value === "object"
                      ? JSON.stringify(selectedCacheItem.value, null, 2)
                      : selectedCacheItem.value}
                  </pre>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => {
                  setShowModal(false);
                  confirmDelete(selectedCacheItem.key, "single");
                }}
              >
                Delete This Item
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                {deleteType === "all"
                  ? "Are you sure you want to clear ALL cache data?"
                  : `Are you sure you want to delete cache item: "${deleteTarget}"?`}
              </p>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={
                  deleteType === "all"
                    ? handleDeleteAll
                    : () => handleDeleteSingle(deleteTarget)
                }
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cache;
